import type {
  DocumentPageMetadataUserEditComment,
  DocumentPageMetadataUserEditEntry,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'

function normalizeEmail(value: string | undefined): string | undefined {
  if (value === undefined) return undefined
  const trimmed = value.trim().toLowerCase()
  return trimmed === '' ? undefined : trimmed
}

export function pageMetadataUserEditCreatedByEmail(
  edit: DocumentPageMetadataUserEditEntry
): string | undefined {
  return normalizeEmail(edit.created_by ?? edit.entry.created_by)
}

function pageMetadataUserEditEventTime(
  edit: DocumentPageMetadataUserEditEntry
): number {
  const raw = edit.entry.created_at ?? edit.entry.updated_at
  if (!raw) return 0
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : 0
}

/** Reason to show when the newest page user-edit — any author — is a deferral. */
export function deferReasonIfLatestUserEditDeferred(
  edits: readonly DocumentPageMetadataUserEditEntry[]
): string | undefined {
  let latest: DocumentPageMetadataUserEditEntry | undefined
  let latestTime = Number.NEGATIVE_INFINITY
  for (const edit of edits) {
    const time = pageMetadataUserEditEventTime(edit)
    if (latest === undefined || time > latestTime) {
      latest = edit
      latestTime = time
    }
  }
  if (latest?.entry.data.status !== 'deferred') return undefined
  const reason = latest.entry.data.defer_reason?.trim()
  return reason ? reason : undefined
}

/** Resume this user’s latest decision without exposing another labeler’s values or notes. */
export function pageMetadataEvalHistoryForUser(
  edits: readonly DocumentPageMetadataUserEditEntry[],
  userEmail: string
): {
  latestEvent: DocumentPageMetadataUserEditEntry | undefined
  comments: DocumentPageMetadataUserEditComment[]
} {
  const comments: DocumentPageMetadataUserEditComment[] = []
  const user = normalizeEmail(userEmail)
  if (user === undefined) return { latestEvent: undefined, comments }

  let winner: DocumentPageMetadataUserEditEntry | undefined
  let winnerTime = Number.NEGATIVE_INFINITY
  for (const edit of edits) {
    if (edit.entry.data.surface !== 'eval') continue
    if (pageMetadataUserEditCreatedByEmail(edit) !== user) continue
    comments.push(...(edit.entry.data.comments ?? []))
    const time = pageMetadataUserEditEventTime(edit)
    if (winner === undefined || time > winnerTime) {
      winner = edit
      winnerTime = time
    }
  }
  comments.sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
  return { latestEvent: winner, comments }
}
